import config from "../config";
import { withoutBearer } from "./tokenStore";

/**
 * Sign-in calls, all through the Shake backend (which proxies VIO):
 *   GET /auth/token  Authorization: <refresh token>  -> access token
 *   GET /users/sync  Authorization: <access token>   -> VIO user (+ org)
 * Errors carry `kind`: "signin" (session unusable — reopen from VIO),
 * "legal" (terms pending in VIO) or "transient" (network / VIO down).
 */
export class AuthError extends Error {
  constructor(kind, message) {
    super(message);
    this.kind = kind;
  }
}

async function get(path, token, fetchImpl) {
  let response;
  try {
    response = await fetchImpl(`${config.apiBaseUrl}${path}`, {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    throw new AuthError("transient", error.message);
  }
  const body = await response.json().catch(() => null);
  return { response, body };
}

const failureKind = (response, body) => {
  if (response.status === 403 && (body?.errorCode || body?.code) === "LEGAL_ACCEPTANCE_REQUIRED") return "legal";
  if (response.status >= 500) return "transient";
  return "signin";
};

export async function exchangeRefreshToken(refreshToken, fetchImpl = fetch) {
  const { response, body } = await get("/auth/token", withoutBearer(refreshToken), fetchImpl);
  const accessToken = withoutBearer(body?.message?.accessToken);
  if (response.ok && body?.status === "Success" && accessToken) return accessToken;
  throw new AuthError(failureKind(response, body), `Token exchange failed (${response.status})`);
}

export async function fetchSessionUser(accessToken, fetchImpl = fetch) {
  const { response, body } = await get("/users/sync", withoutBearer(accessToken), fetchImpl);
  if (response.ok && body?.status === "Success" && body.message?.userId) {
    const m = body.message;
    return {
      userId: String(m.userId),
      username: m.username || m.email || "",
      email: m.email || "",
      tenantId: m.tenantId,
      subCompanyId: m.subCompanyId ?? null,
    };
  }
  throw new AuthError(failureKind(response, body), `User sync failed (${response.status})`);
}

/** Tokens the VIO host put on the iframe URL (Member App page / home section). */
export function readUrlAuth(search = window.location.search) {
  const params = new URLSearchParams(search);
  const refreshToken = withoutBearer(params.get("refreshToken"));
  const accessToken = withoutBearer(params.get("token") || params.get("accessToken"));
  return {
    refreshToken,
    accessToken,
    redirectUrl: params.get("redirectUrl") || null,
    hasTokens: Boolean(refreshToken || accessToken),
  };
}
